// react
import { MouseEvent, useRef, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Button, Grid, PopoverProps } from '@mui/material';

// ui
import { Popover } from '../Popover/Popover';

// icons
import { FilterListOutlined as IconFilterList } from '@mui/icons-material';

// ui
import { FilterProps, Filters } from '../Filters/Filters';

export interface BtnFiltersProps {
	filters: FilterProps[];
	hasClose?: boolean;
	hasLabel?: boolean;
	isOpen?: boolean;
	onClose?: () => void;
	onReset?: () => void;
	PopoverProps?: Partial<PopoverProps>;
	paramsPreserved?: string[];
}

export const BtnFilters = ({
	filters,
	hasClose = true,
	hasLabel = true,
	isOpen,
	onClose,
	onReset,
	PopoverProps,
	paramsPreserved = [],
}: BtnFiltersProps) => {
	// hooks
	const { t } = useTranslation();

	// refs
	const refBtn = useRef<HTMLButtonElement>(null);

	// state
	const [anchorMenu, setAnchorMenu] = useState<HTMLButtonElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	// handlers
	const handleResetFilters = () => {
		// clear search input
		const elQ = document.querySelector('#form-search-q') as HTMLInputElement;

		if (elQ) {
			elQ.value = '';
		}

		if (onReset) {
			onReset();
		}

		// reset search params but preserve specified params
		const searchParamsNew = new URLSearchParams();

		// preserve specified params
		searchParams.forEach((paramValue, paramKey) => {
			if (paramsPreserved.includes(paramKey)) {
				searchParamsNew.set(paramKey, paramValue);
			}
		});

		setSearchParams(searchParamsNew);
		setAnchorMenu(null);
	};

	const handleClose = () => {
		setAnchorMenu(null);
		onClose?.();
	};

	// vars
	const anchorEl = anchorMenu || refBtn.current;
	const isPopoverOpen = Boolean(isOpen) || Boolean(anchorMenu);

	// vars
	const { slotProps = {}, ...popoverPropsRest } = PopoverProps || {};
	const { paper: slotPropsPaper = {} } = slotProps;

	return (
		<>
			<Button
				aria-label={hasLabel ? undefined : t('toggleFilters')}
				color="primary"
				disabled={!filters.length}
				fullWidth
				onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorMenu(e.currentTarget)}
				ref={refBtn}
				startIcon={hasLabel ? <IconFilterList /> : undefined}
				variant="outlined">
				{hasLabel ? t('filter') : <IconFilterList />}
			</Button>
			<Popover
				actions={[
					{
						children: t('reset'),
						onClick: handleResetFilters,
						fullWidth: true,
						GridProps: {
							xs: 6,
						},
						variant: 'outlined',
					},
					{
						children: t('done'),
						fullWidth: true,
						GridProps: {
							xs: 6,
						},
						onClick: handleClose,
						variant: 'contained',
					},
				]}
				hasClose={hasClose}
				PopoverProps={{
					...popoverPropsRest,
					anchorEl,
					onClose: handleClose,
					open: isPopoverOpen,
					slotProps: {
						paper: {
							sx: {
								maxHeight: 500,
							},
							...slotPropsPaper,
						},
					},
				}}
				title={{
					children: t('filter'),
				}}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Filters filters={filters} />
					</Grid>
				</Grid>
			</Popover>
		</>
	);
};
